import { type InvocationOptions, splitRoute, type ToolError } from "@mola/contract";
import { validators } from "./generated";
import type { Operations } from "./generated/types";

export type { Operations } from "./generated/types";

export class ToolClientError extends Error {
  constructor(
    readonly detail: ToolError,
    readonly invocationId?: string,
  ) {
    super(detail.message);
    this.name = "ToolClientError";
  }
}

const codes: readonly string[] = [
  "invalid_input",
  "unauthenticated",
  "denied",
  "not_found",
  "conflict",
  "upstream",
  "timeout",
  "internal",
] satisfies ToolError["code"][];

/** The `{ error }` body of a non-2xx response, or `internal` when it has none. */
function toolError(body: unknown): ToolError {
  const error =
    body && typeof body === "object" && "error" in body
      ? (body.error as Record<string, unknown> | null)
      : undefined;
  if (
    !error ||
    typeof error !== "object" ||
    typeof error.code !== "string" ||
    !codes.includes(error.code) ||
    typeof error.message !== "string" ||
    typeof error.retryable !== "boolean"
  )
    return { code: "internal", message: "Invalid server response", retryable: false };
  return {
    code: error.code as ToolError["code"],
    message: error.message,
    retryable: error.retryable,
    ...(typeof error.retryAfterMs === "number" ? { retryAfterMs: error.retryAfterMs } : {}),
  };
}

/**
 * The request for an operation: its path parameters substituted, the rest as
 * the body of a POST or else the query, the rule the API reads them back with.
 */
function toolRequest(
  key: keyof Operations,
  input: unknown,
): { method: string; path: string; body?: string } {
  const route = splitRoute(key);
  const rest = { ...(input as Record<string, unknown>) };
  const path = route.path.replace(/\{([a-zA-Z0-9]+)\}/g, (_: string, name: string) => {
    const value = rest[name];
    delete rest[name];
    return encodeURIComponent(String(value));
  });
  if (route.method === "POST") return { method: "POST", path, body: JSON.stringify(rest) };
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(rest))
    if (value !== undefined) query.set(key, String(value));
  return { method: route.method, path: query.size ? `${path}?${query}` : path };
}

/** Calls a tool over HTTP and validates its output against the contract. */
export function createInvoker(options: { baseUrl?: string; fetch?: typeof fetch } = {}) {
  const request = options.fetch ?? fetch;
  async function invoke<R extends keyof Operations>(
    route: R,
    input: Operations[R]["input"],
    call: InvocationOptions = {},
  ): Promise<Operations[R]["output"]> {
    const target = toolRequest(route, input);
    const response = await request(`${options.baseUrl ?? ""}${target.path}`, {
      method: target.method,
      credentials: "include",
      headers: {
        ...(target.body === undefined ? {} : { "Content-Type": "application/json" }),
        ...(call.idempotencyKey ? { "Idempotency-Key": call.idempotencyKey } : {}),
      },
      ...(target.body === undefined ? {} : { body: target.body }),
      ...(call.signal ? { signal: call.signal } : {}),
    });
    const body: unknown = await response.json().catch(() => undefined);
    if (response.status === 202)
      throw new ToolClientError(
        {
          code: "conflict",
          message: "Invocation is still running",
          retryable: true,
          retryAfterMs: 500,
        },
        (body as { invocationId?: string } | undefined)?.invocationId,
      );
    const invocationId = response.headers.get("invocation-id") ?? undefined;
    if (!response.ok) throw new ToolClientError(toolError(body), invocationId);
    if (!validators[route].output(body))
      throw new ToolClientError(
        {
          code: "internal",
          message: "Response does not match the tool contract",
          retryable: false,
        },
        invocationId,
      );
    return body as Operations[R]["output"];
  }
  return { invoke };
}
